import axios from 'axios';

// API Base URL - update this to match your backend URL
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

// Endpoints  
const ACCOUNTS_URL = `${API_BASE_URL}/accounts`;
const TRANSACTION_TYPES_URL = `${API_BASE_URL}/transaction-types`;
const TRANSACTIONS_URL = `${API_BASE_URL}/transactions`;
const RECENT_TRANSACTIONS_URL = `${TRANSACTIONS_URL}/recent`;
const SECURITIES_URL = `${API_BASE_URL}/securities`;
const COUNTERPARTIES_URL = `${API_BASE_URL}/counterparties`;
const ISIN_MASTER_URL = `${API_BASE_URL}/isin-master`;

// Money Market Deals API
const MONEY_MARKET_DEALS_URL = `${API_BASE_URL}/money-market-deals`;

// Mark-to-Market API
const MARK_TO_MARKET_URL = `${API_BASE_URL}/mark-to-market`;

// Repo Deals API
const REPO_DEALS_URL = `${API_BASE_URL}/repo-deals`;

// Debug mode - set to true to see detailed logs
const DEBUG = true;

// Log helper
const logDebug = (message, data) => {
  if (DEBUG) {
    console.log(`[API DEBUG] ${message}`, data);
  }
};

/** Auth headers for fetch/axios calls that bypass the shared api instance. */
export function getAuthHeaders(extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  const token = localStorage.getItem('token');
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('user') || 'null');
  } catch (_) {
    user = null;
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (user) {
    headers['x-user-data'] = JSON.stringify(user);
  }
  return headers;
}

export function getCurrentUserId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.id ?? null;
  } catch (_) {
    return null;
  }
}

// Create axios instance with common configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  // Add a timeout to prevent hanging requests
  timeout: 10000
});

// Add request interceptor for authentication and logging
api.interceptors.request.use(config => {
  // Add authentication token if available
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  logDebug(`Request to ${config.url}`, config);
  return config;
}, error => {
  console.error('Request error:', error);
  return Promise.reject(error);
});

// Add response interceptor for logging
api.interceptors.response.use(response => {
  logDebug(`Response from ${response.config.url}`, response.data);
  return response;
}, error => {
  if (error.response) {
    console.error(`API error (${error.response.status}):`, error.response.data);
    // Backend force-logged-out this session (e.g. an admin ran EOD) - clear stored
    // auth and send the user back to login rather than leaving them on a page
    // where every request just keeps failing with 401.
    if (error.response.status === 401 && error.response.data?.sessionInvalidated) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
  } else if (error.request) {
    console.error('No response received:', error.request);
  } else {
    console.error('Request error:', error.message);
  }
  return Promise.reject(error);
});

// Account API
export const accountApi = {
  getAll: async () => {
    try {
      logDebug('Fetching all accounts...');
      const response = await api.get(ACCOUNTS_URL);
      
      // Log and validate response
      const accounts = response.data.data || [];
      logDebug(`Fetched ${accounts.length} accounts`, accounts);
      
      if (accounts.length === 0) {
        console.warn('Warning: No accounts returned from API');
      }
      
      return accounts;
    } catch (error) {
      console.error('Error fetching accounts:', error);
      // Return mock data if the API fails in development
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Using mock account data due to API error');
        return [
          { id: 1, name: 'Savings Account (Mock)', balance: 5000 },
          { id: 2, name: 'Fixed Deposit #1 (Mock)', balance: 10000 },
          { id: 3, name: 'Investment Account (Mock)', balance: 7500 }
        ];
      }
      throw error;
    }
  },
  
  getById: async (id) => {
    try {
      const response = await api.get(`${ACCOUNTS_URL}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching account ${id}:`, error);
      throw error;
    }
  },
  
  create: async (accountData) => {
    try {
      const response = await api.post(ACCOUNTS_URL, accountData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  },
  
  update: async (id, accountData) => {
    try {
      const response = await api.put(`${ACCOUNTS_URL}/${id}`, accountData);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating account ${id}:`, error);
      throw error;
    }
  },
  
  delete: async (id) => {
    try {
      await api.delete(`${ACCOUNTS_URL}/${id}`);
      return true;
    } catch (error) {
      console.error(`Error deleting account ${id}:`, error);
      throw error;
    }
  }
};

// Transaction Type API
export const transactionTypeApi = {
  getAll: async () => {
    try {
      logDebug('Fetching all transaction types...');
      const response = await api.get(TRANSACTION_TYPES_URL);
      
      // Log and validate response
      const types = response.data || [];
      logDebug(`Fetched ${types.length} transaction types`, types);
      
      if (types.length === 0) {
        console.warn('Warning: No transaction types returned from API');
      }
      
      return types;
    } catch (error) {
      console.error('Error fetching transaction types:', error);
      // Return mock data if the API fails in development
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Using mock transaction type data due to API error');
        return [
          { id: 1, name: 'Buy' },
          { id: 2, name: 'Sell' },
          { id: 3, name: 'Transfer' },
          { id: 4, name: 'Dividend' },
          { id: 5, name: 'Interest' }
        ];
      }
      throw error;
    }
  },
  
  getById: async (id) => {
    try {
      const response = await api.get(`${TRANSACTION_TYPES_URL}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching transaction type ${id}:`, error);
      throw error;
    }
  }
};

// Securities API
export const securityApi = {
  getAll: async () => {
    try {
      logDebug('Fetching all securities...');
      const response = await api.get(SECURITIES_URL);
      
      const securities = response.data.data || [];
      logDebug(`Fetched ${securities.length} securities`, securities);
      
      if (securities.length === 0) {
        console.warn('Warning: No securities returned from API');
      }
      
      return securities;
    } catch (error) {
      console.error('Error fetching securities:', error);
      // Return mock data if the API fails in development
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Using mock securities data due to API error');
        return [
          { id: 1, name: 'Treasury Bonds (Mock)', code: 'TB2023' },
          { id: 2, name: 'Corporate Bonds XYZ (Mock)', code: 'XYZ25' },
          { id: 3, name: 'Treasury Bills (Mock)', code: 'TB91' },
          { id: 4, name: 'Fixed Deposit (Mock)', code: 'FD2Y' }
        ];
      }
      throw error;
    }
  },
  
  getById: async (id) => {
    try {
      const response = await api.get(`${SECURITIES_URL}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching security ${id}:`, error);
      throw error;
    }
  }
};

// ISIN Master API
export const isinMasterApi = {
  getAll: async () => {
    try {
      logDebug('Fetching all ISINs...');
      const response = await api.get(ISIN_MASTER_URL);
      
      const isins = response.data.data || [];
      logDebug(`Fetched ${isins.length} ISINs`, isins);
      
      if (isins.length === 0) {
        console.warn('Warning: No ISINs returned from API');
      }
      
      return isins;
    } catch (error) {
      console.error('Error fetching ISINs:', error);
      // Return mock data if the API fails in development
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Using mock ISIN data due to API error');
        return [
          { isin_number: 'IN1234567890', isin_issuer: 'Treasury Bond 2025 (Mock)', issue_date: '2025-01-01' },
          { isin_number: 'IN0987654321', isin_issuer: 'Corporate Bond XYZ (Mock)', issue_date: '2024-06-01' },
          { isin_number: 'IN1122334455', isin_issuer: 'Government Security (Mock)', issue_date: '2023-12-01' }
        ];
      }
      throw error;
    }
  },
  
  getById: async (isin) => {
    try {
      const response = await api.get(`${ISIN_MASTER_URL}/${isin}`);
      return response.data.data || null;
    } catch (error) {
      console.error(`Error fetching ISIN ${isin}:`, error);
      throw error;
    }
  }
};

// Counterparties API
export const counterpartyApi = {
  getAll: async () => {
    try {
      logDebug('Fetching all counterparties...');
      const response = await api.get(COUNTERPARTIES_URL);
      
      const counterparties = response.data.data || [];
      logDebug(`Fetched ${counterparties.length} counterparties`, counterparties);
      
      if (counterparties.length === 0) {
        console.warn('Warning: No counterparties returned from API');
      }
      
      const normalizeCounterpartyId = (cp) => {
        if (cp?.id !== undefined && cp?.id !== null && cp?.id !== '') {
          const n = parseInt(cp.id, 10);
          if (Number.isFinite(n) && n > 0) return n;
        }
        if (cp?.counterparty_id !== undefined && cp?.counterparty_id !== null && cp?.counterparty_id !== '') {
          const n = parseInt(cp.counterparty_id, 10);
          if (Number.isFinite(n) && n > 0) return n;
        }
        const uid = String(cp?.unique_id || '').trim(); // e.g. i7 / j12 / c3
        const m = uid.match(/\d+/);
        if (m && m[0]) {
          const n = parseInt(m[0], 10);
          if (Number.isFinite(n) && n > 0) return n;
        }
        return null;
      };

      // Map to always provide short_name and long_name for frontend
      return counterparties.map(cp => ({
        id: normalizeCounterpartyId(cp),
        unique_id: cp.unique_id,
        short_name: cp.short_name,
        long_name: cp.long_name,
        type: cp.type
      }));
    } catch (error) {
      console.error('Error fetching counterparties:', error);
      // Return mock data if the API fails in development
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Using mock counterparties data due to API error');
        return [
          { id: 1, short_name: 'BOC', long_name: 'Bank of Ceylon (Mock)', type: 'individual' },
          { id: 2, short_name: 'PB', long_name: "People's Bank (Mock)", type: 'individual' },
          { id: 3, short_name: 'COMB', long_name: 'Commercial Bank (Mock)', type: 'individual' },
        ];
      }
      throw error;
    }
  },
  getById: async (id) => {
    try {
      const response = await api.get(`${COUNTERPARTIES_URL}/${id}`);
      const cp = response.data.data;
      return cp ? {
        id: cp.id ?? cp.counterparty_id ?? id,
        unique_id: cp.unique_id,
        short_name: cp.short_name,
        long_name: cp.long_name,
        type: cp.type
      } : null;
    } catch (error) {
      console.error(`Error fetching counterparty ${id}:`, error);
      throw error;
    }
  }
};

// Transaction API
export const transactionApi = {
  // Get all transactions
  getAll: async () => {
    try {
      const response = await api.get(TRANSACTIONS_URL);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }
  },
  
  // Get recent transactions
  getRecent: async (limit = 10) => {
    try {
      const response = await api.get(`${RECENT_TRANSACTIONS_URL}?limit=${limit}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching recent transactions:', error);
      return [];
    }
  },
  
  // Get transactions by account ID
  getByAccountId: async (accountId) => {
    try {
      const response = await api.get(`${TRANSACTIONS_URL}/account/${accountId}`);
      return response.data.data || [];
    } catch (error) {
      console.error(`Error fetching transactions for account ${accountId}:`, error);
      return [];
    }
  },
  
  // Get transaction by ID
  getById: async (id) => {
    try {
      const response = await api.get(`${TRANSACTIONS_URL}/${id}`);
      return response.data.data || null;
    } catch (error) {
      console.error(`Error fetching transaction ${id}:`, error);
      return null;
    }
  },
  
  // Create a new transaction
  create: async (transactionData) => {
    try {
      const response = await api.post(TRANSACTIONS_URL, transactionData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  },
  
  // Update a transaction by deal_number
  update: async (dealNumber, transactionData, config = {}) => {
    try {
      const response = await api.put(`${TRANSACTIONS_URL}/${dealNumber}`, transactionData, config);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating transaction with deal_number ${dealNumber}:`, error);
      throw error;
    }
  },
  
  // Delete a transaction
  delete: async (id) => {
    try {
      const response = await api.delete(`${TRANSACTIONS_URL}/${id}`);
      return response.data.success;
    } catch (error) {
      console.error(`Error deleting transaction ${id}:`, error);
      throw error;
    }
  }
};

// Money Market Deals API
export const moneyMarketApi = {
  // Fetch voucher PDF for a deal
  async fetchVoucherPdf(dealNumber) {
    const url = `${API_BASE_URL}/money-market/${dealNumber}/voucher`;
    const response = await axios.get(url, { responseType: 'blob' });
    return response.data;
  },
  // Get all money market deals
  getAll: async () => {
    try {
      const response = await api.get(MONEY_MARKET_DEALS_URL);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching money market deals:', error);
      return [];
    }
  },

  // Get a money market deal by deal number
  getById: async (dealNumber) => {
    try {
      const response = await api.get(`${MONEY_MARKET_DEALS_URL}/${dealNumber}`);
      return response.data.data || null;
    } catch (error) {
      console.error(`Error fetching money market deal ${dealNumber}:`, error);
      return null;
    }
  },

  // Create a new money market deal
  create: async (dealData) => {
    try {
      const response = await api.post(MONEY_MARKET_DEALS_URL, dealData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating money market deal:', error);
      throw error;
    }
  },

  // Update a money market deal (for status/authorization)
  update: async (dealNumber, dealData) => {
    try {
      const response = await api.put(`${MONEY_MARKET_DEALS_URL}/${dealNumber}`, dealData);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating money market deal ${dealNumber}:`, error);
      throw error;
    }
  }
};

// Mark-to-Market API
export const markToMarketApi = {
  // Get all mark-to-market data
  getAll: async () => {
    try {
      logDebug('Fetching all mark-to-market data...');
      const response = await api.get(MARK_TO_MARKET_URL);
      
      const data = response.data.data || [];
      logDebug(`Fetched ${data.length} mark-to-market records`, data);
      
      return data;
    } catch (error) {
      console.error('Error fetching mark-to-market data:', error);
      return [];
    }
  },

  // Get mark-to-market data by series
  getBySeries: async (series) => {
    try {
      logDebug(`Fetching mark-to-market data for series: ${series}`);
      const response = await api.get(`${MARK_TO_MARKET_URL}/series/${series}`);
      
      const data = response.data.data;
      logDebug(`Fetched mark-to-market data for series ${series}`, data);
      
      return data;
    } catch (error) {
      console.error(`Error fetching mark-to-market data for series ${series}:`, error);
      return null;
    }
  },

  // Get dirty price for a specific ISIN/series
  getDirtyPrice: async (isinNumber) => {
    try {
      logDebug(`Fetching dirty price for ISIN: ${isinNumber}`);
      
      // First get all mark-to-market data
      const allData = await markToMarketApi.getAll();
      logDebug(`Retrieved ${allData.length} records from Mark-to-Market API`);
      
      // Find the record that matches the ISIN number (trim both sides —
      // some isin_master / MTM rows historically carried trailing spaces).
      const needle = String(isinNumber || '').trim();
      const record = allData.find(
        (item) => String(item.isin_number || '').trim() === needle
      );
      
      if (record && record.dirty_price) {
        logDebug(`Found dirty price for ISIN ${isinNumber}: ${record.dirty_price}`);
        return parseFloat(record.dirty_price);
      } else {
        logDebug(`No dirty price found for ISIN ${isinNumber}`);
        logDebug(`Available ISINs: ${allData.map(r => r.isin_number).join(', ')}`);
        return null;
      }
    } catch (error) {
      console.error(`Error fetching dirty price for ISIN ${isinNumber}:`, error);
      return null;
    }
  }
};

// Repo Deals API
export const repoDealsApi = {
  // Get all repo deals
  getAll: async () => {
    try {
      logDebug('Fetching all repo deals...');
      const response = await api.get(REPO_DEALS_URL);
      
      const deals = response.data.data || [];
      logDebug(`Fetched ${deals.length} repo deals`, deals);
      
      return deals;
    } catch (error) {
      console.error('Error fetching repo deals:', error);
      return [];
    }
  },

  // Get recent repo deals
  getRecent: async (limit = 10) => {
    try {
      logDebug(`Fetching recent ${limit} repo deals...`);
      const response = await api.get(`${REPO_DEALS_URL}?limit=${limit}`);
      
      const deals = response.data.data || [];
      logDebug(`Fetched ${deals.length} recent repo deals`, deals);
      
      return deals;
    } catch (error) {
      console.error('Error fetching recent repo deals:', error);
      return [];
    }
  },

  // Get repo deal by ID
  getById: async (id) => {
    try {
      const response = await api.get(`${REPO_DEALS_URL}/${id}`);
      return response.data.data || null;
    } catch (error) {
      console.error(`Error fetching repo deal ${id}:`, error);
      return null;
    }
  },

  // Create a new repo deal
  create: async (dealData) => {
    try {
      const response = await api.post(REPO_DEALS_URL, dealData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating repo deal:', error);
      throw error;
    }
  },

  // Update repo deal
  update: async (id, dealData) => {
    try {
      const response = await api.put(`${REPO_DEALS_URL}/${id}`, dealData);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating repo deal ${id}:`, error);
      throw error;
    }
  },

  // Delete repo deal
  delete: async (id) => {
    try {
      await api.delete(`${REPO_DEALS_URL}/${id}`);
      return true;
    } catch (error) {
      console.error(`Error deleting repo deal ${id}:`, error);
      throw error;
    }
  },

  updateApproval: async (id, { action, comment }, user) => {
    try {
      const response = await api.patch(
        `${REPO_DEALS_URL}/${id}/approval`,
        { action, comment },
        {
          headers: {
            'x-user-data': JSON.stringify(user || {})
          }
        }
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error updating repo approval ${id}:`, error);
      throw error;
    }
  }
};

// Pre-Approval Maturity API
const PRE_APPROVAL_URL = `${API_BASE_URL}/maturity/pre-approval`;

export const preApprovalApi = {
  // Get deals available for pre-approval
  getPreApprovalDeals: async (filters = {}) => {
    try {
      logDebug('Fetching pre-approval deals...', filters);
      const params = new URLSearchParams();
      if (filters.productType) params.append('productType', filters.productType);
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      if (filters.counterparty) params.append('counterparty', filters.counterparty);
      if (filters.status) params.append('status', filters.status);
      
      const response = await api.get(`${PRE_APPROVAL_URL}/deals?${params}`);
      logDebug(`Fetched ${response.data.data?.length || 0} pre-approval deals`);
      return response.data;
    } catch (error) {
      console.error('Error fetching pre-approval deals:', error);
      throw error;
    }
  },

  // Pre-approve a deal
  preApproveDeal: async (productType, dealId, user) => {
    try {
      logDebug(`Pre-approving deal ${dealId} of type ${productType}`);
      const response = await api.post(
        `${PRE_APPROVAL_URL}/${productType}/${dealId}`,
        {},
        {
          headers: {
            'x-user-data': JSON.stringify(user)
          }
        }
      );
      logDebug('Deal pre-approved successfully');
      return response.data;
    } catch (error) {
      console.error('Error pre-approving deal:', error);
      throw error;
    }
  },

  // Authorizer approves pre-approval
  approvePreApproval: async (productType, dealId, user) => {
    try {
      logDebug(`Approving pre-approval for deal ${dealId} of type ${productType}`);
      const response = await api.put(
        `${PRE_APPROVAL_URL}/${productType}/${dealId}/approve`,
        {},
        {
          headers: {
            'x-user-data': JSON.stringify(user)
          }
        }
      );
      logDebug('Pre-approval approved successfully');
      return response.data;
    } catch (error) {
      console.error('Error approving pre-approval:', error);
      throw error;
    }
  },

  // Authorizer rejects pre-approval
  rejectPreApproval: async (productType, dealId, user) => {
    try {
      logDebug(`Rejecting pre-approval for deal ${dealId} of type ${productType}`);
      const response = await api.put(
        `${PRE_APPROVAL_URL}/${productType}/${dealId}/reject`,
        {},
        {
          headers: {
            'x-user-data': JSON.stringify(user)
          }
        }
      );
      logDebug('Pre-approval rejected successfully');
      return response.data;
    } catch (error) {
      console.error('Error rejecting pre-approval:', error);
      throw error;
    }
  },

  // Get pre-approved deals for blotter
  getPreApprovedDeals: async (filters = {}) => {
    try {
      logDebug('Fetching pre-approved deals for blotter...', filters);
      const params = new URLSearchParams();
      if (filters.productType) params.append('productType', filters.productType);
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      if (filters.counterparty) params.append('counterparty', filters.counterparty);
      
      const response = await api.get(`${PRE_APPROVAL_URL}/blotter?${params}`);
      logDebug(`Fetched ${response.data.data?.length || 0} pre-approved deals`);
      return response.data;
    } catch (error) {
      console.error('Error fetching pre-approved deals:', error);
      throw error;
    }
  }
};

// Trial Balance / GSec Balance Sheet: sourced from an external accounting API
// (http://98.91.201.168/api) whose CORS policy only allows its own AWS Amplify
// origin, so the browser can't call it directly. Routed through this app's own
// backend instead (routes/externalAccountingRoutes.js), which relays the
// request server-to-server (no CORS involved) authenticated with a dedicated
// service account, not this app's own user tokens.
export const trialBalanceApi = {
  // GET /api/external-accounting/trial-balance?startDate=&endDate=&_ts=
  getTrialBalance: async ({ startDate, endDate } = {}) => {
    try {
      logDebug('Fetching trial balance...', { startDate, endDate });
      const response = await api.get('/external-accounting/trial-balance', {
        params: { startDate, endDate, _ts: Date.now() }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching trial balance:', error);
      throw error;
    }
  },

  // GET /api/external-accounting/trial-balance/account/:accountCode?startDate=&endDate=
  getAccountDetail: async ({ accountCode, startDate, endDate } = {}) => {
    try {
      logDebug('Fetching trial balance account detail...', { accountCode, startDate, endDate });
      const response = await api.get(
        `/external-accounting/trial-balance/account/${encodeURIComponent(accountCode)}`,
        { params: { startDate, endDate } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching trial balance account detail:', error);
      throw error;
    }
  }
};

// Keep the exact name requested (trialBalanceAPI) as an alias for trialBalanceApi.
export const trialBalanceAPI = trialBalanceApi;

export const gsecEntriesApi = {
  // GET /api/external-accounting/gsec-entries/balance-sheet?startDate=&endDate=
  getBalanceSheet: async ({ startDate, endDate } = {}) => {
    try {
      logDebug('Fetching GSec entries balance sheet...', { startDate, endDate });
      const response = await api.get('/external-accounting/gsec-entries/balance-sheet', {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching GSec entries balance sheet:', error);
      throw error;
    }
  },

  // GET /api/external-accounting/gsec-entries/balance-sheet/account/:accountCode?startDate=&endDate=
  getAccountDetail: async ({ accountCode, startDate, endDate } = {}) => {
    try {
      logDebug('Fetching GSec balance sheet account detail...', { accountCode, startDate, endDate });
      const response = await api.get(
        `/external-accounting/gsec-entries/balance-sheet/account/${encodeURIComponent(accountCode)}`,
        { params: { startDate, endDate } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching GSec balance sheet account detail:', error);
      throw error;
    }
  }
};

// Keep the exact name requested (gsecEntriesAPI) as an alias for gsecEntriesApi.
export const gsecEntriesAPI = gsecEntriesApi;

// Export the configured axios instance for use in components
export { api };

export default {
  account: accountApi,
  transactionType: transactionTypeApi,
  transaction: transactionApi,
  securities: securityApi,
  counterparties: counterpartyApi,
  isinMaster: isinMasterApi,
  moneyMarket: moneyMarketApi,
  markToMarket: markToMarketApi,
  repoDeals: repoDealsApi,
  preApproval: preApprovalApi
};